import { type Space, bytesToNDigits } from '@mail/cross-sizes-utils';
import { equals, filter, path, sortBy, values } from 'ramda';
import { actionRegExp } from 'reactApp/appHelpers/actionSettings';
import { autumnPromo } from 'reactApp/appHelpers/featuresHelpers/features/autumnPromo';
import { blackFriday2024 } from 'reactApp/appHelpers/featuresHelpers/features/blackFriday2024';
import { cheapTariffOnlyYearDesktop } from 'reactApp/appHelpers/featuresHelpers/features/cheapTariffOnlyYear';
import { cyberMonday2025 } from 'reactApp/appHelpers/featuresHelpers/features/cyberMonday2025';
import { mayPromo2025, mayPromo2025YearProduct } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import type { Product, Tariff } from 'reactApp/types/Billing';
import type { ITariffsByTab } from 'reactApp/ui/TariffSectionNew/TariffsSection.types';
import { isMonthPeriod, parsePeriod, simplifyPeriod } from 'reactApp/utils/Period';
import { pretextSPlural } from 'reactApp/utils/pluralHelpers';

import { cheapestProductIDs } from './products.const';

export const SINGLE_QUOTA_REGEX = /.*_single_quota*/;

export const SINGLE_QUOTA_DISKO_REGEX = /.*_single_quota_disko_cloud*/;

export const DEFAULT_PRIMARY_PRODUCT = 137438953472; // 128GB

function isExist<T>(item: T | null | undefined): item is T {
    return Boolean(item);
}

export const sortBySpace = <T extends { space: Space }>(products: Record<string, T | undefined>) =>
    // @ts-ignore
    sortBy(path(['space', 'original']), values(products).filter(isExist));

export const normalizeData = (tariffs) => {
    const list: Record<string, Tariff | undefined> = {};

    Object.keys(tariffs).forEach((id) => {
        const group = tariffs[id];

        const { services, products } = group;
        const space = bytesToNDigits(services.quota, 3);
        const gift = services.gift && {
            space: bytesToNDigits(services.gift.gift_quota, 3),
            period: services.gift.gift_interval,
            id: services.gift.giftible_id,
        };

        // специальный семейный тариф, который открывает доступ к семейным папкам/альбомам
        const isFamily = Boolean(services?.cloud?.family?.albums && services?.cloud?.family?.folders);

        list[id] = {
            id,
            turbo: /^pro?\d/.test(products[0].product_id),
            isSingleQuota: Boolean(services.mailru),
            isPromoCode: products.some((product) => product.is_promo),
            available: products.every((product) => product.available),
            space,
            flags: {
                upload: !!services?.flags?.UFLAG_PAID_UPLOAD,
                paidFeatures: !!services?.flags?.UFLAG_PAID_ACCOUNT,
            },
            gift,
            isProTariff: !!services.disko && !!services.pro,
            isFamily,
            isOnlyFeatures: space.original === SPACE_LIST.kb_1,
            products: products.map((product) => ({
                price: product.price,
                id: product.product_id,
                period: product.period,
                available: product.available,
                isPromo: product.is_promo,
                hasTrial: product.has_trial_period,
                trialPeriod: product.trial_period,
                isProfessional: !!services.pro,
                isDisko: !!services.disko,
                hasDiskO: !!services.disko && !!services.pro,
                hasDiskOAutoRenewal: !!services.disko,
                // Пока считаем что наличие санты это наличие vkCombo
                isVkCombo: !!services.santa,
                santa: services.santa,
                // Триалы у которых есть еще скидочный период
                isForceDiscountTrial: product.has_trial_period && product.has_discount_period,
                // "предоплаченный" триал, не отнимает квоту при отвязке карты / отключеннии автопродления
                isPrepaid: product.is_prepaid,
                hasDiscount: product.has_discount_period,
                discountPeriod: product.discount_period,
                ...(product?.discount_period_count && { discountPeriodCount: product.discount_period_count }),
                discountPrice: product.discount_price,
                space,
                turbo: /^pro?\d/.test(product.product_id),
                isSingleQuota: Boolean(services.mailru),
                gift,
                isFamily,
                isOnlyFeatures: space.original === SPACE_LIST.kb_1,
            })),
        };
    });

    return list;
};

export const checkProductListEquals =
    (oldData) =>
    ({ data }) =>
        !equals(normalizeData(data), oldData);

export const AUTUMN_PROMO_TARIFFS_REGEX = /.*_october_action.*/;
export const isAutumnPromoProductsAvailable = (productId: string) => autumnPromo && AUTUMN_PROMO_TARIFFS_REGEX.test(productId);
export const AUTUMN_PROMO_DESKTOP_TARIFFS_SIZES = [
    SPACE_LIST.gb_256,
    cheapTariffOnlyYearDesktop === 'b' ? SPACE_LIST.gb_512 : undefined,
    SPACE_LIST.tb_1,
    SPACE_LIST.tb_2,
    SPACE_LIST.tb_4,
];

export const BLACK_FRIDAY_2024_TARIFFS_REGEX = /.*_bf_2024.*/;
export const isBlackFriday2024ProductsAvailable = (productId: string) => blackFriday2024 && BLACK_FRIDAY_2024_TARIFFS_REGEX.test(productId);
export const BLACK_FRIDAY_2024_5GB_TARIFF_ID = 'W5TB_bf_2024';

export const CYBER_MONDAY_2025_TARIFFS_REGEX = /.*_cybermonday_2025.*/;
export const isCyberMonday2025ProductsAvailable = (productId: string) => cyberMonday2025 && CYBER_MONDAY_2025_TARIFFS_REGEX.test(productId);
export const CYBER_MONDAY_2025_4TB_TARIFF_ID = 'W4TB_cybermonday_2025';
export const CYBER_MONDAY_2025_DESKTOP_TARIFFS_SIZES = [
    SPACE_LIST.gb_256,
    SPACE_LIST.gb_512,
    SPACE_LIST.tb_1,
    SPACE_LIST.tb_2,
    SPACE_LIST.tb_4,
];

export const isMayPromo2025ProductsAvailable = (productId: string) => mayPromo2025 && mayPromo2025YearProduct === productId;

// tempexp-18683-next-line
export const FLEXIBLE_QUOTA_TARIFFS_REGEX = /.*_flex_space$/;

// tempexp_16481-start
export const AUTOUPLOAD_10TB_REGEX = /^W.*_(autoupload_action(_discount)?|autoupload_v2_discount|upsale_10tgift_discount).*$/;
// @TODO: пришлось захардкодить массив тк нужно было синхронизировать вебвью мобилки между экспами. Переделать если эксп взлетит.
export const AUTOUPLOAD_10TB_TARIFFS = [
    'W64GB_1y_autoupload_action_discount',
    'W128GB_1y_autoupload_action_discount',
    'W256GB_1y_autoupload_action_discount',
    'W512GB_1y_autoupload_action_discount',
    'W1TB_1y_autoupload_action_discount_single_quota_cloud',
    'W2TB_1y_autoupload_action_discount_single_quota_cloud',
    'W4TB_1y_autoupload_action_discount_single_quota_cloud',
    'W64GB_1m_autoupload_action',
    'W128GB_1m_autoupload_action',
    'W256GB_1m_autoupload_action',
    'W512GB_1m_autoupload_action',
    'W1TB_1m_autoupload_action_single_quota_cloud',
    'W2TB_1m_autoupload_action_single_quota_cloud',
    'W4TB_1m_autoupload_action_single_quota_cloud',
    'W256GB_1m_autoupload_v2_discount',
    'W1TB_1m_autoupload_v2_discount',
    'W2TB_1m_autoupload_v2_discount',
];
// tempexp_16481-end

export const TRIAL_REGEX = /.*(prepaid|trial)+/;

export const isTrial = (productId?: string) => productId?.includes('prepaid') || productId?.includes('trial');

export const YEAR_COST_DECREASE_REGEX = /W\d{3,4}G1y_2021/;

export const YEAR_COST_DECREASE_SINGLE_QUOTA_REGEX = /W\d{1,4}[TG]1y_.*(single_quota_discount50_cloud)/;

export const isNotSpecialProduct = (product) =>
    !product.products.some((product) => product.hasTrial) &&
    !product.products.some((product) => product.isPromo) &&
    !actionRegExp.test(product.id);

export const SPACE_SIZES = {
    _32: SPACE_LIST.gb_32,
    _64: SPACE_LIST.gb_64,
    _128: SPACE_LIST.gb_128,
    _256: SPACE_LIST.gb_256,
    _512: SPACE_LIST.gb_512,
    _1024: SPACE_LIST.tb_1,
    _2048: SPACE_LIST.tb_2,
    _4096: SPACE_LIST.tb_4,
};

export const getPretextPeriodForProduct = (period: string) => {
    const matchedPeriod = parsePeriod(period);
    if (!matchedPeriod) {
        return '';
    }
    const [count] = simplifyPeriod(Number(matchedPeriod[1]), matchedPeriod[2]);

    return pretextSPlural(count + 1);
};

export const filterTariffsByQuotaAndPeriod = (tariff, quota, period) => {
    if (tariff.space.original !== quota) {
        return false;
    }

    return Boolean(tariff.products.find((product) => product.period === period));
};

export const isTariffForExclude = (tariff: Tariff, quota: number, excludePro?: boolean): boolean => {
    if (tariff.isProTariff && !excludePro) {
        return false;
    }

    return tariff?.space?.original === quota;
};

export const getTariffsByQuotaHelper = (tariffs: Tariff[], quota: number) =>
    filter((tariff) => path(['space', 'original'], tariff) === +quota, tariffs);

// tempexp_16338-start
export const replaceProductInTariff = (tariffs: Tariff[], tariffsWithProductToReplace: Tariff[]) => {
    return tariffs.map((tariff) => {
        const tariffWithIncreasedPrice = getTariffsByQuotaHelper(tariffsWithProductToReplace, tariff.space.original)[0] as Tariff;

        if (!tariffWithIncreasedPrice) {
            return tariff;
        }

        const index = tariff.products.findIndex((product) => product.period === tariffWithIncreasedPrice?.products[0].period);

        if (index === -1) {
            return tariff;
        }

        const products = [...tariff.products];
        products[index] = tariffWithIncreasedPrice.products[0];

        return {
            ...tariff,
            products,
        };
    });
};
// tempexp_16338-end

/**
 * tempexp_16510
 * Фильтрует и сортирует массив тарифов на основании массива размеров.
 *
 * @param tariffs - массив тарифов
 * @param quotasInGigabytes - массив размеров в Гигабайтах в нужном порядке
 *
 * @returns
 */
export const filterAndSortTariffsByQuota = (tariffs: Tariff[], quotasInGigabytes: number[]) => {
    const filtered = tariffs.filter((item) => {
        const itemSpaceInGigabytes = item?.space?.original / SPACE_LIST.gb_1;
        return quotasInGigabytes.includes(itemSpaceInGigabytes);
    });
    filtered.sort((a, b) => {
        const aSpaceInGigabytes = a?.space?.original / SPACE_LIST.gb_1;
        const bSpaceInGigabytes = b?.space?.original / SPACE_LIST.gb_1;
        return quotasInGigabytes.indexOf(aSpaceInGigabytes) - quotasInGigabytes.indexOf(bSpaceInGigabytes);
    });

    return filtered;
};

/**
 * Фильтрует и сортирует массив тарифов на основании списка ГОДОВЫХ продуктов.
 *
 * @param tariffs - массив тарифов
 * @param yearProducts - массив id годовых продуктов в нужном порядке
 *
 * @returns
 */
export const sortTariffsByYearList = (tariffs: ITariffsByTab[], yearProducts: string[]) => {
    return tariffs.sort((a, b) => {
        const aProduct = a.products.yearProduct;
        const bProduct = b.products.yearProduct;

        if (aProduct && bProduct) {
            return yearProducts.indexOf(aProduct.id) - yearProducts.indexOf(bProduct.id);
        }

        return 0;
    });
};

/**
 * Сортирует массив по id на основании порядка в параметре list
 *
 * @param list
 */
export const getComparatorByIdFromList = (list: string[]) => (a, b) => list.indexOf(a.id) - list.indexOf(b.id);

/**
 * Возвращает продукт из списка на основании квоты и периода
 *
 * @param tariffs - массив тарифов
 * @param quota - размер квоты продукта
 * @param period - период действия продукта
 */
export const getProductFromTariffListByQuotaAndPeriod = (tariffs: Tariff[], quota, period): Product | undefined => {
    const products = tariffs.find((tariff) => tariff.space.original === quota)?.products;
    return products?.find((product) => product.period === period);
};

/* tempexp_16598-start */
export const isCheapestProduct = (productId: string) => Object.values(cheapestProductIDs).includes(productId);
/* tempexp_16598-end */

export const hasMonthTariff = (tariffs: Tariff[]) =>
    tariffs.some((tariff) => tariff.products.some((product) => isMonthPeriod(product.period)));
